'use client';

import { useState, useEffect } from 'react';
import { X, Download, Link as LinkIcon, FolderPlus, Clock, Palette, Edit3, Lock, Film, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { MuxClipPlayer } from '@/components/moments/MuxClipPlayer';
import { GenericVideoPlayer } from './GenericVideoPlayer';
import { SaveToCollectionDialog } from '@/components/collections/SaveToCollectionDialog';
import { CaptionStyleModal } from './CaptionStyleModal';
import { ExportQualityModal } from './ExportQualityModal';
import { StyledCaptionOverlay } from './StyledCaptionOverlay';
import { SelectedMoment } from './MomentoContext';
import { useMomento } from '@/hooks/useMomento';
import { toast } from 'sonner';
import type { CaptionStyleName } from '@/lib/utils/caption-styles';
import { ExportQuality, getDefaultQualityForPlan } from '@/lib/utils/export-quality';
import { MemoTranscriptEditor } from '@/components/memo/MemoTranscriptEditor';

// Moment type configuration (same as in MomentsGrid)
const MOMENT_TYPE_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  chapter: { label: 'Chapter', color: 'bg-blue-500', icon: '📖' },
  highlight: { label: 'Highlight', color: 'bg-yellow-500', icon: '✨' },
  quote: { label: 'Quote', color: 'bg-purple-500', icon: '💬' },
  topic: { label: 'Topic', color: 'bg-green-500', icon: '🏷️' },
  action: { label: 'Action', color: 'bg-red-500', icon: '✅' },
  faq: { label: 'Q&A', color: 'bg-indigo-500', icon: '❓' },
  sentiment: { label: 'Sentiment', color: 'bg-pink-500', icon: '😊' },
  playbook: { label: 'Playbook', color: 'bg-orange-500', icon: '📋' },
  // B2B Marketing types
  testimonial: { label: 'Testimonial', color: 'bg-emerald-500', icon: '💚' },
  case_study: { label: 'Case Study', color: 'bg-cyan-500', icon: '🏆' },
  pain_point: { label: 'Pain Point', color: 'bg-rose-500', icon: '⚠️' },
  objection_handling: { label: 'Objection', color: 'bg-violet-500', icon: '🛡️' },
  social_proof: { label: 'Social Proof', color: 'bg-amber-500', icon: '📈' },
};

interface MomentoVideoPanelProps {
  moment: SelectedMoment;
  onClose: () => void;
  onExportRequest?: (moment: SelectedMoment) => void;
  domainId: string;
  creditBalance?: {
    balance: number;
    planType: 'free' | 'plus' | 'pro' | 'enterprise';
    isLowBalance: boolean;
  } | null;
}

export function MomentoVideoPanel({ moment, onClose, onExportRequest, domainId, creditBalance }: MomentoVideoPanelProps) {
  const { setActiveMemo } = useMomento();
  const [showCollectionDialog, setShowCollectionDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isCreatingMemo, setIsCreatingMemo] = useState(false);

  // Check if user can export (Plus+ plan required)
  const isFreePlan = creditBalance?.planType === 'free';
  const [selectedCaptionStyle, setSelectedCaptionStyle] = useState<CaptionStyleName>('minimal');
  const [showCaptionStyleModal, setShowCaptionStyleModal] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState<ExportQuality>(
    getDefaultQualityForPlan(creditBalance?.planType || 'free')
  );
  const [showQualityModal, setShowQualityModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [existingExport, setExistingExport] = useState<{
    hasExport: boolean;
    status: string | null;
    exportUrl: string | null;
    exportId?: string;
  } | null>(null);
  const [isCheckingExport, setIsCheckingExport] = useState(false);

  // Transcript state
  const [transcriptData, setTranscriptData] = useState<{
    words: any[];
    utterances?: any[];
    speakerMappings?: Record<string, string>;
    transcriptId?: string;
  } | null>(null);
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(true);

  // Fetch transcript on mount
  useEffect(() => {
    const fetchTranscript = async () => {
      if (!moment.postId) {
        setIsLoadingTranscript(false);
        return;
      }

      setIsLoadingTranscript(true);
      try {
        const response = await fetch(`/api/posts/${moment.postId}/transcript`);

        if (response.ok) {
          const data = await response.json();
          setTranscriptData(data);
        } else {
          console.log('[MomentoVideoPanel] No transcript available');
        }
      } catch (error) {
        console.error('[MomentoVideoPanel] Error fetching transcript:', error);
      } finally {
        setIsLoadingTranscript(false);
      }
    };

    fetchTranscript();
  }, [moment.postId]);

  // Check if this moment has already been exported
  useEffect(() => {
    const checkExistingExport = async () => {
      if (!moment.postId) return;

      setIsCheckingExport(true);
      try {
        const response = await fetch(
          `/api/moments/export-status?postId=${moment.postId}&startTime=${moment.startTime}&endTime=${moment.endTime}`
        );

        if (response.ok) {
          const data = await response.json();
          setExistingExport(data);
        }
      } catch (error) {
        console.error('[MomentoVideoPanel] Error checking export status:', error);
      } finally {
        setIsCheckingExport(false);
      }
    };

    checkExistingExport();
  }, [moment.postId, moment.startTime, moment.endTime]);

  const handleAddToCollection = () => {
    setShowCollectionDialog(true);
  };

  const handleSaveSpeakerName = async (speakerId: string, speakerName: string) => {
    if (!moment.postId || !transcriptData?.transcriptId) {
      toast.error('Unable to save speaker name');
      return;
    }

    try {
      const updatedMappings = {
        ...(transcriptData.speakerMappings || {}),
        [speakerId]: speakerName,
      };

      const response = await fetch(`/api/posts/${moment.postId}/speakers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcriptId: transcriptData.transcriptId,
          speakerMappings: updatedMappings,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save speaker name');
      }

      // Optimistically update local state
      setTranscriptData((prev) => ({
        ...prev!,
        speakerMappings: updatedMappings,
      }));

      toast.success(`Speaker labeled as "${speakerName}"`);
    } catch (error) {
      console.error('[MomentoVideoPanel] Error saving speaker name:', error);
      toast.error('Failed to save speaker name');
    }
  };

  const handleCreateMemo = async () => {
    if (!moment.postId) {
      toast.error('Cannot create memo: Missing video information');
      return;
    }

    setIsCreatingMemo(true);
    try {
      const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${String(secs).padStart(2, '0')}`;
      };

      const response = await fetch('/api/memo/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourcePostId: moment.postId,
          startTime: moment.startTime,
          endTime: moment.endTime,
          title: moment.title || `Memo ${formatTime(moment.startTime)}-${formatTime(moment.endTime)}`,
          domainId: domainId,
          initialEdits: {
            deleteWords: [],
            replaceText: [],
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error(error.error || 'Failed to create memo');
        return;
      }

      const result = await response.json();

      toast.success('Memo created! Opening editor...');

      // Fetch full memo and activate
      const memoResponse = await fetch(`/api/memo/${result.memoId}`);
      const memoData = await memoResponse.json();

      if (memoData.memo) {
        setActiveMemo(memoData.memo);
      }
    } catch (error) {
      console.error('[MomentoVideoPanel] Create memo error:', error);
      toast.error('Failed to create memo');
    } finally {
      setIsCreatingMemo(false);
    }
  };

  const handleExport = async () => {
    if (moment.isPreview && moment.previewUrl) {
      window.open(moment.previewUrl, '_blank');
      toast.success('Opening download link...');
      return;
    }

    if (!moment.postId) {
      toast.error('Cannot export: Missing post ID');
      return;
    }

    setIsExporting(true);

    try {
      const response = await fetch('/api/moments/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          postId: moment.postId,
          startTime: moment.startTime,
          endTime: moment.endTime,
          captionStyle: selectedCaptionStyle,
          quality: selectedQuality,
          title: moment.title,
          domainId: domainId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 402) {
          toast.error(result.error || 'Insufficient credits', {
            description: result.upgradeUrl
              ? 'Upgrade your plan to export moments'
              : `You need ${result.creditsRequired || 2} credits. You have ${result.creditsAvailable || 0}.`,
            action: result.upgradeUrl ? {
              label: 'Upgrade Plan',
              onClick: () => window.open(result.upgradeUrl, '_blank'),
            } : undefined,
          });
        } else {
          toast.error(result.error || 'Export failed');
        }
        return;
      }

      toast.success('Export started!', {
        description: `Processing will take ~${Math.ceil((result.estimatedTime || 120) / 60)} minutes. You'll receive an email when ready.`,
      });

      console.log('[MomentoVideoPanel] Export started:', {
        exportId: result.exportId,
        estimatedTime: result.estimatedTime,
        creditsRemaining: result.creditsRemaining,
      });

    } catch (error) {
      console.error('[MomentoVideoPanel] Export error:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyLink = () => {
    const url = moment.previewUrl || `https://stream.mux.com/${moment.playbackId}.m3u8?start=${moment.startTime}&end=${moment.endTime}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  if (!moment.postId) {
    console.warn('[MomentoVideoPanel] Missing postId for moment:', moment);
  }

  return (
    <>
      <div className="h-full flex flex-col bg-background">
      {/* Header - Compact with context */}
      <div className="flex items-center justify-between px-3 py-2 border-b flex-shrink-0">
        <div className="flex-1 min-w-0 mr-3">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-xs truncate">
              {moment.title || 'Video Moment'}
            </h3>
            {moment.momentType && MOMENT_TYPE_CONFIG[moment.momentType] && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0 h-4 flex items-center gap-1"
              >
                <span>{MOMENT_TYPE_CONFIG[moment.momentType].icon}</span>
                <span>{MOMENT_TYPE_CONFIG[moment.momentType].label}</span>
              </Badge>
            )}
          </div>
          {moment.postTitle && (
            <p className="text-[10px] text-muted-foreground truncate">
              From: {moment.postTitle}
            </p>
          )}
          <p className="text-[10px] text-muted-foreground">
            {/* Show "Full Video" for full videos, time range for moments */}
            {moment.momentType === 'full_video' || (moment.startTime === 0 && moment.endTime === 0) ? (
              'Full Video'
            ) : (
              `${formatTime(moment.startTime)} - ${formatTime(moment.endTime)}`
            )}
            {moment.similarity && (
              <span className="ml-2">· Match: {Math.round(moment.similarity * 100)}%</span>
            )}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="flex-shrink-0 h-6 w-6"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      {/* Video Player */}
      <div className="w-full bg-black flex-shrink-0 max-h-[35vh] flex items-center justify-center relative overflow-hidden">
        {/* Preview Badge */}
        {moment.isPreview && moment.expiresAt && (
          <Badge
            variant="secondary"
            className="absolute top-4 right-4 z-10 flex items-center gap-1"
          >
            <Clock className="h-3 w-3" />
            Preview • Expires {formatExpiry(moment.expiresAt)}
          </Badge>
        )}

        {/* Use GenericVideoPlayer for Rendi previews, MuxClipPlayer for MUX streams */}
        {moment.previewUrl ? (
          <>
            <GenericVideoPlayer
              src={moment.previewUrl}
              autoPlay={true}
              controls={true}
              className="w-full h-full"
              onTimeUpdate={(time) => setCurrentTime(time)}
            />
            {/* Caption overlay preview (if transcript available) */}
            {moment.transcript && (
              <StyledCaptionOverlay
                currentTime={currentTime}
                transcript={moment.transcript}
                captionStyle={selectedCaptionStyle}
                editPlan={moment.editPlan}
              />
            )}
          </>
        ) : (
          <>
            <MuxClipPlayer
              playbackId={moment.playbackId}
              startTime={moment.startTime}
              endTime={moment.endTime}
              autoPlay={true}
              controls={true}
              className="w-full h-full"
              onTimeUpdate={(time) => setCurrentTime(time)}
            />
            {/* Caption overlay preview (if transcript available) */}
            {moment.transcript && (
              <StyledCaptionOverlay
                currentTime={currentTime}
                transcript={moment.transcript}
                captionStyle={selectedCaptionStyle}
                editPlan={moment.editPlan}
              />
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t flex gap-2 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowCaptionStyleModal(true)}
          title="Choose caption style for export"
        >
          <Palette className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowQualityModal(true)}
          title="Choose export quality"
        >
          <Film className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={handleAddToCollection}
        >
          <FolderPlus className="h-4 w-4 mr-2" />
          Add to Collection
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={handleCreateMemo}
          disabled={isCreatingMemo}
          title="Create a memo to edit this moment"
        >
          <Edit3 className="h-4 w-4 mr-2" />
          {isCreatingMemo ? 'Creating...' : 'Edit'}
        </Button>
        {existingExport?.hasExport && existingExport.status === 'completed' && existingExport.exportUrl ? (
          <div className="flex-1 flex gap-2">
            <Button
              variant="default"
              size="sm"
              className="flex-1"
              onClick={() => window.open(existingExport.exportUrl!, '_blank')}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={isExporting}
              title="Export again with different settings"
            >
              Retry
            </Button>
          </div>
        ) : isFreePlan ? (
          // Free Plan: Locked export with billing redirect
          <Button
            variant="outline"
            size="sm"
            className="flex-1 border-purple-500 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
            onClick={() => {
              window.location.href = '/dashboard/settings/billing';
            }}
          >
            <Lock className="w-4 h-4 mr-2" />
            Export - Upgrade Required
          </Button>
        ) : (
          // Paid Plan: Normal export with quality badge
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={handleExport}
            disabled={isExporting || isCheckingExport}
          >
            <Download className="h-4 w-4 mr-2" />
            <span className="flex items-center gap-2">
              {moment.isPreview ? 'Download' :
               existingExport?.status === 'processing' ? 'Exporting...' :
               existingExport?.status === 'pending' ? 'Processing...' :
               'Export'}
              {!isExporting && !moment.isPreview && (
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-primary/20"
                >
                  {selectedQuality === '4k' ? '4K' : selectedQuality === '1080' ? '1080p' : selectedQuality === 'hd' ? '720p' : 'SD'}
                </Badge>
              )}
            </span>
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyLink}
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Transcript Section - Always Visible */}
      <div className="flex-1 flex flex-col overflow-hidden border-t">
        {isLoadingTranscript ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : transcriptData && transcriptData.words && transcriptData.words.length > 0 ? (
          <>
            {/* Speaker Labeling Alert */}
            {transcriptData.utterances && (() => {
              const unlabeledSpeakers = transcriptData.utterances
                .map((u: any) => u.speaker)
                .filter((s: string, i: number, self: string[]) =>
                  self.indexOf(s) === i && !transcriptData.speakerMappings?.[s]
                );

              return unlabeledSpeakers.length > 0 ? (
                <Alert className="mx-4 mt-4 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700">
                  <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                  <AlertTitle className="text-yellow-800 dark:text-yellow-300">Speaker Labeling Needed</AlertTitle>
                  <AlertDescription className="text-yellow-700 dark:text-yellow-400">
                    I found {unlabeledSpeakers.length} unlabeled speaker{unlabeledSpeakers.length > 1 ? 's' : ''} (
                    {unlabeledSpeakers.map((s: string) => `Speaker ${s}`).join(', ')}
                    ). Click the speaker badges below to add names for authentic posts.
                  </AlertDescription>
                </Alert>
              ) : null;
            })()}

            {/* Legend */}
            <div className="px-4 py-3 flex items-center gap-6 text-xs text-muted-foreground border-b bg-muted/30">
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 bg-red-200 dark:bg-red-900/30 rounded" />
                <span>Deleted</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 bg-yellow-100 dark:bg-yellow-900/30 rounded" />
                <span>Caption Edit</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 bg-blue-100 dark:bg-blue-900/30 rounded" />
                <span>Playing Now</span>
              </div>
            </div>

            {/* Transcript Editor */}
            <div className="flex-1 overflow-auto">
              <MemoTranscriptEditor
                words={transcriptData.words}
                utterances={transcriptData.utterances}
                speakerMappings={transcriptData.speakerMappings || {}}
                editPlan={{ cuts: [], captions: [] }}
                currentTime={currentTime}
                onSeekToWord={(time) => {
                  // Seek logic would go here if we expose player controls
                  console.log('Seek to:', time);
                }}
                isEditingMode={false}
                onSpeakerEdit={handleSaveSpeakerName}
              />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
            No transcript available for this video
          </div>
        )}
      </div>
    </div>

      {/* Caption Style Modal */}
      <CaptionStyleModal
        open={showCaptionStyleModal}
        onOpenChange={setShowCaptionStyleModal}
        selectedStyle={selectedCaptionStyle}
        onSelectStyle={setSelectedCaptionStyle}
      />

      {/* Export Quality Modal */}
      <ExportQualityModal
        open={showQualityModal}
        onOpenChange={setShowQualityModal}
        selectedQuality={selectedQuality}
        onSelectQuality={setSelectedQuality}
        currentPlan={creditBalance?.planType || 'free'}
      />

      {/* Save to Collection Dialog */}
      {moment.postId && (
        <SaveToCollectionDialog
          open={showCollectionDialog}
          onOpenChange={setShowCollectionDialog}
          momentData={{
            postId: moment.postId,
            startTime: moment.startTime,
            endTime: moment.endTime,
            title: moment.title,
            addedFromSearch: true,
          }}
          onSaved={() => {
            toast.success('Moment saved to collection');
          }}
        />
      )}
    </>
  );
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatExpiry(expiresAt: string): string {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const hoursRemaining = Math.floor((expiry.getTime() - now.getTime()) / (1000 * 60 * 60));

  if (hoursRemaining < 1) {
    const minutesRemaining = Math.floor((expiry.getTime() - now.getTime()) / (1000 * 60));
    return `${minutesRemaining}m`;
  }

  return `${hoursRemaining}h`;
}
